/**
 * Internal audits: badge tones, query keys and small helpers shared by the
 * list page, the detail page and its dialogs.
 */
import { useQuery } from '@tanstack/react-query';
import {
  AUDIT_ITEM_STATUS_LABELS,
  CONFORMITY_LABELS,
  FINDING_CLASSIFICATION_LABELS,
  FINDING_STATUS_LABELS,
  INTERNAL_AUDIT_STATUS_LABELS,
  type AuditItemStatus,
  type Conformity,
  type FindingClassification,
  type FindingStatus,
  type InternalAuditCounts,
  type InternalAuditFinding,
  type InternalAuditItem,
  type InternalAuditProgress,
  type InternalAuditStatus,
  type OrgMember,
} from '@grc/types';
import { ApiRequestError, apiFetch } from '../../lib/api.js';
import { Badge, type BadgeTone } from '../../components/ui.js';

// ------------------------------------------------------------------- keys
export const auditKey = (auditId: string) => ['internal-audit', auditId] as const;
export const itemsKey = (auditId: string) => ['internal-audit-items', auditId] as const;
export const auditsListKey = (orgId: string) => ['internal-audits', orgId] as const;

// ------------------------------------------------------------------ tones
export const AUDIT_STATUS_TONE: Record<InternalAuditStatus, BadgeTone> = {
  planned: 'neutral',
  in_progress: 'info',
  completed: 'success',
  approved: 'purple',
};

export const ITEM_STATUS_TONE: Record<AuditItemStatus, BadgeTone> = {
  pending: 'neutral',
  running: 'info',
  done: 'success',
  failed: 'danger',
  skipped: 'neutral',
};

export const CONFORMITY_TONE: Record<Conformity, BadgeTone> = {
  not_audited: 'neutral',
  conforming: 'success',
  minor_nonconformity: 'warning',
  major_nonconformity: 'danger',
  observation: 'info',
  opportunity_for_improvement: 'primary',
  not_evidenced: 'neutral',
};

export const CLASSIFICATION_TONE: Record<FindingClassification, BadgeTone> = {
  major_nonconformity: 'danger',
  minor_nonconformity: 'warning',
  observation: 'info',
  opportunity_for_improvement: 'primary',
  conforming: 'success',
};

export const FINDING_STATUS_TONE: Record<FindingStatus, BadgeTone> = {
  open: 'info',
  accepted: 'primary',
  rejected: 'neutral',
  actioned: 'warning',
  closed: 'success',
};

/** Short badge labels for the finding counts (M / m / O / OFI). */
const COUNT_BADGES: Array<{ key: keyof InternalAuditCounts; short: string; label: string; tone: BadgeTone }> = [
  { key: 'major', short: 'M', label: 'Major nonconformities', tone: 'danger' },
  { key: 'minor', short: 'm', label: 'Minor nonconformities', tone: 'warning' },
  { key: 'observation', short: 'O', label: 'Observations', tone: 'info' },
  { key: 'ofi', short: 'OFI', label: 'Opportunities for improvement', tone: 'primary' },
];

// ----------------------------------------------------------------- badges
export function AuditStatusBadge({ status }: { status: InternalAuditStatus }) {
  return <Badge tone={AUDIT_STATUS_TONE[status]}>{INTERNAL_AUDIT_STATUS_LABELS[status]}</Badge>;
}

export function ItemStatusBadge({ status }: { status: AuditItemStatus }) {
  return <Badge tone={ITEM_STATUS_TONE[status]}>{AUDIT_ITEM_STATUS_LABELS[status]}</Badge>;
}

export function ConformityBadge({ conformity }: { conformity: Conformity }) {
  return <Badge tone={CONFORMITY_TONE[conformity]}>{CONFORMITY_LABELS[conformity]}</Badge>;
}

export function ClassificationBadge({ classification }: { classification: FindingClassification }) {
  return (
    <Badge tone={CLASSIFICATION_TONE[classification]}>
      {FINDING_CLASSIFICATION_LABELS[classification]}
    </Badge>
  );
}

export function FindingStatusBadge({ status }: { status: FindingStatus }) {
  return <Badge tone={FINDING_STATUS_TONE[status]}>{FINDING_STATUS_LABELS[status]}</Badge>;
}

/** M / m / O / OFI count badges; zero counts are omitted, none at all shows a dash. */
export function FindingCountBadges({ counts }: { counts: InternalAuditCounts }) {
  const shown = COUNT_BADGES.filter((b) => counts[b.key] > 0);
  if (shown.length === 0) return <span aria-label="No findings">—</span>;
  return (
    <span className="audit-badges">
      {shown.map((b) => (
        <Badge key={b.key} tone={b.tone}>
          <span title={b.label} aria-label={`${counts[b.key]} ${b.label.toLowerCase()}`}>
            {b.short} {counts[b.key]}
          </span>
        </Badge>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- helpers
export function countsOf(findings: InternalAuditFinding[]): InternalAuditCounts {
  const counts: InternalAuditCounts = { major: 0, minor: 0, observation: 0, ofi: 0, conforming: 0 };
  for (const f of findings) {
    if (f.classification === 'major_nonconformity') counts.major += 1;
    else if (f.classification === 'minor_nonconformity') counts.minor += 1;
    else if (f.classification === 'observation') counts.observation += 1;
    else if (f.classification === 'opportunity_for_improvement') counts.ofi += 1;
    else counts.conforming += 1;
  }
  return counts;
}

/** Share of the clauses the AI auditor (or a person) has dealt with. */
export function progressPercent(p: InternalAuditProgress): number {
  if (p.total === 0) return 0;
  return Math.round(((p.done + p.failed + p.skipped) / p.total) * 100);
}

/** A 'running' item older than ten minutes was abandoned by the run that claimed it. */
export function isStale(item: InternalAuditItem): boolean {
  if (item.status !== 'running' || !item.runAt) return false;
  const started = new Date(item.runAt).getTime();
  return Number.isNaN(started) ? false : Date.now() - started > 10 * 60 * 1000;
}

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiRequestError ? err.message : fallback;
}

/** Field-level issues of a `validation_failed` response, keyed by path. */
export function issuesOf(err: unknown): Record<string, string> {
  if (!(err instanceof ApiRequestError)) return {};
  const issues = err.details?.issues;
  if (!Array.isArray(issues)) return {};
  const out: Record<string, string> = {};
  for (const issue of issues) {
    if (issue && typeof issue === 'object') {
      const { path, message } = issue as { path?: unknown; message?: unknown };
      const key = Array.isArray(path) ? path.join('.') : String(path ?? '');
      if (key && typeof message === 'string' && !out[key]) out[key] = message;
    }
  }
  return out;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Members of the organisation (for auditor and owner selects). */
export function useMembers(orgId: string, enabled = true) {
  return useQuery({
    queryKey: ['members', orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${orgId}/members`),
    enabled,
  });
}

/**
 * Options for a member select: active members, plus the current value when it
 * points at someone who has since been disabled or removed, so an existing
 * choice is never silently dropped.
 */
export function memberOptions(
  members: OrgMember[] | undefined,
  currentId: string | null | undefined,
  currentName: string | null | undefined,
): Array<{ id: string; label: string }> {
  const list = (members ?? [])
    .filter((m) => m.status === 'active' || m.user.id === currentId)
    .map((m) => ({ id: m.user.id, label: m.status === 'active' ? m.user.name : `${m.user.name} (disabled)` }));
  if (currentId && !list.some((m) => m.id === currentId)) {
    list.push({ id: currentId, label: `${currentName ?? 'Former member'} (no longer a member)` });
  }
  return list;
}
